import React from "react";
import {
  Wifi,
  WifiOff,
  RefreshCw,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface Chat8StatusBarProps {
  isConnected: boolean;
  isReconnecting: boolean;
  error: string | null;
  connectionHealth?: "healthy" | "degraded" | "unhealthy";
  reconnectAttempts?: number;
}

export const Chat8StatusBar: React.FC<Chat8StatusBarProps> = ({
  isConnected,
  isReconnecting,
  error,
  connectionHealth = "unhealthy",
  reconnectAttempts = 0,
}) => {
  React.useEffect(() => {
    console.log("[Chat8StatusBar] 🔍 Props recebidos:", {
      isConnected,
      isReconnecting,
      error,
      connectionHealth,
      reconnectAttempts,
      timestamp: new Date().toISOString()
    });
  }, [isConnected, isReconnecting, error, connectionHealth, reconnectAttempts]);

  const getStatusConfig = () => {
    if (isReconnecting) {
      return {
        icon: <RefreshCw className="w-4 h-4 animate-spin" />,
        text: "Reconectando...",
        badgeClass: "bg-amber-500/20 text-amber-300 border-amber-500/30",
      };
    }
    if (error) {
      return {
        icon: <AlertCircle className="w-4 h-4" />,
        text: "Erro de conexão",
        badgeClass: "bg-red-500/20 text-red-300 border-red-500/30",
      };
    }
    if (isConnected) {
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        text: "Conectado",
        badgeClass: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
      };
    }
    return {
      icon: <WifiOff className="w-4 h-4" />,
      text: "Desconectado",
      badgeClass: "bg-white/10 text-silver border-white/20",
    };
  };

  const getHealthConfig = () => {
    switch (connectionHealth) {
      case "healthy":
        return { text: "Saudável", class: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" };
      case "degraded":
        return { text: "Degradado", class: "bg-amber-500/20 text-amber-300 border-amber-500/30" };
      case "unhealthy":
        return { text: "Não saudável", class: "bg-red-500/20 text-red-300 border-red-500/30" };
      default:
        return { text: "Desconhecido", class: "bg-white/10 text-silver border-white/20" };
    }
  };

  const statusConfig = getStatusConfig();
  const healthConfig = getHealthConfig();

  return (
    <footer
      className="flex items-center justify-between px-4 py-2 bg-navy-700/50 border-t border-white/10 backdrop-blur-sm"
      role="contentinfo"
      aria-label="Barra de status da conexão"
    >
      {/* Connection Status */}
      <div className="flex items-center gap-2">
        <div className="text-silver">
          {statusConfig.icon}
        </div>
        <Badge variant="outline" className={cn("text-xs font-medium", statusConfig.badgeClass)}>
          {statusConfig.text}
        </Badge>
      </div>
      {/* Debug information */}
      <div className="flex items-center gap-2">
        <Badge 
          variant="outline" 
          className={cn("text-xs", healthConfig.class)}
          title="Saúde da conexão"
        >
          {healthConfig.text}
        </Badge>
        {reconnectAttempts > 0 && (
          <Badge
            variant="outline"
            className="text-xs bg-amber-500/20 text-amber-300 border-amber-500/30"
            title="Tentativas de reconexão"
          >
            Tentativas: {reconnectAttempts}
          </Badge>
        )}
        {error && (
          <Badge
            variant="outline"
            className="text-xs bg-red-500/20 text-red-300 border-red-500/30"
            title={error}
          >
            Erro
          </Badge>
        )}
      </div>
    </footer>
  );
};
